import type { LeftStop, StatusFilter, StreetSide } from './walkStops'

/** [Spec §8] One prefs key replaces canvasser_map_filter + the two *_view_mode keys. */
export const WALK_PREFS_KEY = 'canvasser_walk_prefs'
const LEGACY_FILTER = 'canvasser_map_filter'
const LEGACY_VIEW_KEYS = ['canvasser_contacts_view_mode', 'canvasser_map_view_mode']

export type ViewMode = 'households' | 'contacts' | 'byStreet'
export type WalkSurface = 'list' | 'map'
export interface WalkPrefs {
  filter: StatusFilter; viewMode: ViewMode; surface: WalkSurface
  /** [Phase 4] The door flow's "Left literature" tick is remembered across doors. */
  leftLiterature: boolean
  /** Odd/even side of the street, remembered so a pair splitting a turf sets it once. */
  side: StreetSide
  /** [A3 §6] The one-time "Re-plan" tip has been dismissed on this device. */
  replanTipSeen: boolean
}

const DEFAULTS: WalkPrefs = { filter: 'remaining', viewMode: 'households', surface: 'list', leftLiterature: false, side: 'all', replanTipSeen: false }
const FILTERS: readonly StatusFilter[] = ['remaining', 'revisit', 'all']
const VIEWS: readonly ViewMode[] = ['households', 'contacts', 'byStreet']
const SURFACES: readonly WalkSurface[] = ['list', 'map']
const SIDES: readonly StreetSide[] = ['all', 'odd', 'even']

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback
}

function legacyPrefs(): Partial<WalkPrefs> {
  const out: Partial<WalkPrefs> = {}
  const f = localStorage.getItem(LEGACY_FILTER)
  if (f === 'tryAgain') out.filter = 'revisit'
  else if (f === 'all' || f === 'remaining') out.filter = f
  const v = localStorage.getItem(LEGACY_VIEW_KEYS[0])
  if (v === 'contact') out.viewMode = 'contacts'
  else if (v === 'byStreet') out.viewMode = 'byStreet'
  return out
}

export function readWalkPrefs(): WalkPrefs {
  try {
    const raw = localStorage.getItem(WALK_PREFS_KEY)
    if (!raw) return { ...DEFAULTS, ...legacyPrefs() }
    const p = JSON.parse(raw) as Record<string, unknown>
    return {
      filter: pick(p.filter, FILTERS, DEFAULTS.filter),
      viewMode: pick(p.viewMode, VIEWS, DEFAULTS.viewMode),
      surface: pick(p.surface, SURFACES, DEFAULTS.surface),
      leftLiterature: p.leftLiterature === true,
      side: pick(p.side, SIDES, DEFAULTS.side),
      replanTipSeen: p.replanTipSeen === true,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

export function writeWalkPrefs(p: WalkPrefs): void {
  try { localStorage.setItem(WALK_PREFS_KEY, JSON.stringify(p)) } catch { /* private mode: prefs are a convenience */ }
}

/** One-time boot sweep of the pre-Phase-2 keys (spec §8). Call AFTER readWalkPrefs has carried them over. */
export function sweepLegacyWalkKeys(): void {
  try { for (const k of [LEGACY_FILTER, ...LEGACY_VIEW_KEYS]) localStorage.removeItem(k) } catch { /* ignore */ }
}

const leftKey = (turfId: string) => `canvasser_walk_left_${turfId}`

/** [§15.1 S5] The stop the canvasser left the walk screen for (sessionStorage: dies with the tab/logout). */
export function rememberLeftStop(turfId: string, left: LeftStop): void {
  try { sessionStorage.setItem(leftKey(turfId), JSON.stringify(left)) } catch { /* ignore */ }
}

/** Passes the parsed object through, so the optional stop signature (`sig`) survives the round-trip. */
export function readLeftStop(turfId: string): LeftStop | null {
  try {
    const raw = sessionStorage.getItem(leftKey(turfId))
    if (!raw) return null
    const v = JSON.parse(raw) as LeftStop
    return typeof v?.key === 'string' && typeof v?.state === 'string' ? v : null
  } catch {
    return null
  }
}

const turfKey = (campaignId: string) => `canvasser_walk_turf_${campaignId}`

/** [CF-1 P3] The assignment key the canvasser chose (`turf:<id>` / `call:<id>`), per campaign, so returning
 *  from a door lands on the same walk (sessionStorage: dies with the tab/logout). Same storage key as the
 *  old chosen-turf: a legacy bare turf GUID reads back as `turf:<guid>`. */
export function rememberChosenAssignment(campaignId: string, key: string): void {
  try { sessionStorage.setItem(turfKey(campaignId), key) } catch { /* ignore */ }
}

export function readChosenAssignment(campaignId: string): string | null {
  try {
    const v = sessionStorage.getItem(turfKey(campaignId))
    if (!v) return null
    return v.includes(':') ? v : `turf:${v}`
  } catch { return null }
}

export function clearChosenAssignment(campaignId: string): void {
  try { sessionStorage.removeItem(turfKey(campaignId)) } catch { /* ignore */ }
}


/** [Spec §8: kept key] The script language toggle, remembered across doors and sessions. */
const SCRIPT_LANGUAGE_KEY = 'canvasser_script_language'
export type ScriptLanguage = 'en' | 'es'

export function readScriptLanguage(): ScriptLanguage {
  try { return localStorage.getItem(SCRIPT_LANGUAGE_KEY) === 'es' ? 'es' : 'en' } catch { return 'en' }
}

export function writeScriptLanguage(l: ScriptLanguage): void {
  try { localStorage.setItem(SCRIPT_LANGUAGE_KEY, l) } catch { /* private mode: a convenience */ }
}
